import { Input, InputLabel, InputMessage } from '@crehana/compass.ui/v3/input';
import {
  Textarea,
  TextareaLabel,
  TextareaMessage,
} from '@crehana/compass.ui/v3/textarea';
import type {
  TimeOvertimeQuantificationType,
  TimeOvertimeTramo,
} from '@/generated/graphql/types';

import { useTranslation } from '../../../i18n';
import { formatMinutes } from '../../../utils';
import {
  calculateRoundedOvertimeMinutes,
  parseAmountOrZero,
  REASON_MAX_LENGTH,
} from '../utils';

type Props = {
  overtimeText: string;
  onOvertimeChange: (value: string) => void;
  compensationText: string;
  onCompensationChange: (value: string) => void;
  reason: string;
  onReasonChange: (value: string) => void;
  overtimeConfigActive: boolean;
  compensationAvailable: boolean;
  quantificationType: TimeOvertimeQuantificationType | null | undefined;
  quantificationTramos: ReadonlyArray<
    Pick<TimeOvertimeTramo, 'enabled' | 'rounding_direction'>
  >;
  quantificationBlockMinutes: number | null | undefined;
  showOvertimeFormatError: boolean;
  showOvertimeRequiredError: boolean;
  showCompensationFormatError: boolean;
  showCompensationRequiredError: boolean;
  showAmountsExceedDayError: boolean;
  showReasonRequiredError: boolean;
  showReasonMaxLengthError: boolean;
};

export const RequestAmountReasonFields = ({
  overtimeText,
  onOvertimeChange,
  compensationText,
  onCompensationChange,
  reason,
  onReasonChange,
  overtimeConfigActive,
  compensationAvailable,
  quantificationType,
  quantificationTramos,
  quantificationBlockMinutes,
  showOvertimeFormatError,
  showOvertimeRequiredError,
  showCompensationFormatError,
  showCompensationRequiredError,
  showAmountsExceedDayError,
  showReasonRequiredError,
  showReasonMaxLengthError,
}: Props) => {
  const { t } = useTranslation();

  // TIM-663 CAMBIO B — the rounding preview reads the CHOSEN shift's own
  // overtime configuration; there is no separate query for it.
  // `overtime_config_active` gates whether the preview applies at all.
  const roundingHint = (text: string): string | null => {
    if (!overtimeConfigActive) {
      return null;
    }
    const minutes = parseAmountOrZero(text);
    if (!minutes) {
      return null;
    }
    const rounded = calculateRoundedOvertimeMinutes(
      minutes,
      quantificationType,
      quantificationTramos,
      quantificationBlockMinutes,
    );
    if (rounded === minutes) {
      return null;
    }
    return formatMinutes(rounded);
  };

  const overtimeRoundedValue = roundingHint(overtimeText);
  const compensationRoundedValue = roundingHint(compensationText);

  return (
    <>
      <div className="tw-grid tw-grid-cols-2 tw-cds-gap-5">
        <div className="tw-flex tw-flex-col tw-cds-gap-2">
          <InputLabel>{t('CREATE_DIALOG_OVERTIME_LABEL')}</InputLabel>
          <Input
            type="text"
            inputMode="numeric"
            placeholder={t('CREATE_DIALOG_AMOUNT_PLACEHOLDER')}
            value={overtimeText}
            error={showOvertimeFormatError || showOvertimeRequiredError}
            onChange={e => onOvertimeChange(e.target.value)}
          />
          {showOvertimeFormatError && (
            <InputMessage error>{t('VALIDATION_AMOUNT_FORMAT')}</InputMessage>
          )}
          {showOvertimeRequiredError && (
            <InputMessage error>{t('VALIDATION_REQUIRED')}</InputMessage>
          )}
          {!showOvertimeFormatError &&
            !showOvertimeRequiredError &&
            overtimeRoundedValue && (
              <span className="tw-cds-font-helper-text-01 tw-cds-text-subtle">
                {t('CREATE_DIALOG_ROUNDING_PREVIEW', {
                  interpolation: { value: overtimeRoundedValue },
                })}
              </span>
            )}
        </div>

        {compensationAvailable && (
          <div className="tw-flex tw-flex-col tw-cds-gap-2">
            <InputLabel>{t('CREATE_DIALOG_COMPENSATION_LABEL')}</InputLabel>
            <Input
              type="text"
              inputMode="numeric"
              placeholder={t('CREATE_DIALOG_AMOUNT_PLACEHOLDER')}
              value={compensationText}
              error={
                showCompensationFormatError || showCompensationRequiredError
              }
              onChange={e => onCompensationChange(e.target.value)}
            />
            {showCompensationFormatError && (
              <InputMessage error>{t('VALIDATION_AMOUNT_FORMAT')}</InputMessage>
            )}
            {showCompensationRequiredError && (
              <InputMessage error>{t('VALIDATION_REQUIRED')}</InputMessage>
            )}
            {!showCompensationFormatError &&
              !showCompensationRequiredError &&
              compensationRoundedValue && (
                <span className="tw-cds-font-helper-text-01 tw-cds-text-subtle">
                  {t('CREATE_DIALOG_ROUNDING_PREVIEW', {
                    interpolation: { value: compensationRoundedValue },
                  })}
                </span>
              )}
          </div>
        )}
      </div>

      {showAmountsExceedDayError && (
        <span className="tw-cds-font-helper-text-01 tw-cds-text-feedback-alert">
          {t('VALIDATION_AMOUNTS_EXCEED_DAY')}
        </span>
      )}

      <div className="tw-flex tw-flex-col tw-cds-gap-2">
        <TextareaLabel required>
          {t('CREATE_DIALOG_REASON_LABEL')}
        </TextareaLabel>
        <Textarea
          rows={3}
          maxLength={REASON_MAX_LENGTH}
          placeholder={t('CREATE_DIALOG_REASON_PLACEHOLDER')}
          value={reason}
          error={showReasonRequiredError || showReasonMaxLengthError}
          onChange={e => onReasonChange(e.target.value)}
        />
        {showReasonRequiredError && (
          <TextareaMessage error>
            {t('VALIDATION_REASON_REQUIRED')}
          </TextareaMessage>
        )}
        {showReasonMaxLengthError && (
          <TextareaMessage error>
            {t('VALIDATION_REASON_MAX_LENGTH')}
          </TextareaMessage>
        )}
      </div>
    </>
  );
};
